/**
 * detectedEditFactory.js — Pure helpers that turn a detected content-detector
 * `block` (see contentDetector.js) into the edit object shape useEdits expects.
 *
 * Extracted verbatim (Task 7) from the Viewer's per-page detection loop so it
 * can be shared between the click-to-promote candidate path (Viewer.jsx) and
 * unit-tested in isolation. No React, no DOM — safe to import from a worker
 * or a plain test file.
 */

/**
 * Build per-line rich-text segments for a detected text block so each line
 * renders at its detected font size. A line with multiple RUNS (mixed
 * font/size within one baseline) maps to consecutive segments with no '\n'
 * between them; the explicit '\n' segment stays BETWEEN LINES ONLY — the
 * contenteditable layer depends on it for line breaks.
 *
 * Returns `undefined` when the block has no `lineSegments` (older/synthetic
 * callers) so useEdits' auto-migration-from-content-string fallback applies.
 *
 * @param {object} block — a TextBlock from contentDetector.js
 * @returns {Array<{text: string, styles: object}>|undefined}
 */
export function buildSegments(block) {
  if (!block.lineSegments) return undefined;
  return block.lineSegments.flatMap((line, idx, arr) => {
    const runs = line.runs && line.runs.length > 0
      ? line.runs
      : [{ text: line.text, fontSize: line.fontSize, fontFamily: line.fontFamily }];
    const runSegs = runs.map((run) => ({
      text: run.text,
      styles: {
        fontFamily: run.fontFamily || line.fontFamily || block.fontFamily,
        fontSize: run.fontSize || line.fontSize || block.fontSize,
        bold: run.bold ?? line.bold ?? block.bold ?? false,
        italic: run.italic ?? line.italic ?? block.italic ?? false,
        underline: false,
        strikethrough: false,
        color: run.color || block.color,
      },
    }));
    // Add newline segment between lines (not between runs)
    return idx < arr.length - 1
      ? [...runSegs, { text: '\n', styles: { ...runSegs[runSegs.length - 1].styles } }]
      : runSegs;
  });
}

/**
 * Map a detected content-detector `block` (text or image) to the edit object
 * literal useEdits' addEdit/addEdits expect. Shared by the (legacy, being
 * retired) whole-document bulk-detect path and the new click-to-promote
 * candidate path — both must produce byte-identical edits for a given block.
 *
 * @param {object} block — a TextBlock from contentDetector.js
 * @param {(block: object) => Array|undefined} segmentsBuilder — injected so
 *   callers/tests can swap the segments strategy without coupling to the
 *   module-level buildSegments implementation.
 * @returns {object} an edit object ready for useEdits' addEdit/addEdits
 */
export function blockToEdit(block, segmentsBuilder = buildSegments) {
  return {
    type: 'text',
    source: 'detected',
    rotationLocked: block.rotated === true,
    // Stamps the detector's block id onto the edit so candidate visibility
    // can be DERIVED (Bug B fix) instead of the promoted candidate being
    // permanently deleted from candidatesByPage on promotion. New optional
    // field — sidecar-safe (spreads through overlayMetadata like any other
    // edit property; older sidecars simply lack it, which is fine since
    // visibleCandidates treats a missing sourceBlockId as "no match").
    sourceBlockId: block.id,
    pageNum: block.pageNum,
    x: block.x,
    y: block.y,
    width: block.width,
    height: block.height,
    content: block.content,
    segments: segmentsBuilder(block),
    styles: {
      fontFamily: block.fontFamily,
      fontSize: block.fontSize,
      color: block.color,
      alignment: block.alignment,
      bold: block.bold || false,
      italic: block.italic || false,
      backgroundColor: 'transparent',
      // Real detected leading (contentDetector's per-block median baseline
      // delta) when available; 1.3 remains the fallback for blocks with no
      // computed value (e.g. synthetic/legacy callers in tests). Without
      // this, tighter-leaded documents visibly inflated the moment a block
      // became interactive (transparent overlay → real text at 1.3), and
      // the inflated layout baked into the saved PDF.
      lineHeight: block.lineHeight || 1.3,
    },
    originalBounds: block.originalBounds,
    sourceTextBounds: block.sourceTextBounds,
  };
}

/**
 * Derive which detected-block CANDIDATES should render, given the current
 * edits array. (Bug B fix — "block unselectable after promote→delete→undo".)
 *
 * Root cause this replaces: `handlePromoteCandidate` used to permanently
 * REMOVE a candidate from `candidatesByPage` on promotion (`filter((b) => b
 * !== block)`). Undo only restores useEdits' edit-array snapshots — it knows
 * nothing about candidatesByPage — so undoing PAST a promotion left the block
 * with neither a live edit NOR a candidate: invisible to every click handler
 * until the file was reloaded.
 *
 * The fix: never destroy candidate state. A candidate stays in
 * candidatesByPage forever (until the file/page changes); this function
 * derives its VISIBILITY purely from whether a live edit currently claims it
 * via `sourceBlockId` (stamped by `blockToEdit` above). Deleting the edit
 * (tombstoning OR undo removing it from the array entirely) automatically
 * makes the candidate visible/selectable again — no separate "restore the
 * candidate" bookkeeping needed anywhere.
 *
 * Tombstoned edits (`deleted: true`) STILL suppress their candidate — a
 * deleted detected edit keeps its mask/whiteout covering the original block
 * (see MaskLayer / saveAllChanges), so the candidate underneath must stay
 * hidden or the user would see a redundant, confusingly-clickable hit target
 * sitting on top of already-"deleted" text. Only when the EDIT ITSELF is
 * gone from the array (never added, or removed by undo) does the candidate
 * reappear.
 *
 * @param {Array<object>} candidates — detector blocks for one page (each has `.id`)
 * @param {Array<object>} edits — the full edits array (any page — callers
 *   typically pass the already-page-filtered `candidatesByPage.get(pageNum)`
 *   as `candidates`, but `edits` can be the whole document's edits since
 *   `sourceBlockId` values are unique across pages by construction)
 * @returns {Array<object>} the subset of `candidates` with no claiming edit
 */
export function visibleCandidates(candidates, edits) {
  if (!candidates || candidates.length === 0) return candidates || [];
  if (!edits || edits.length === 0) return candidates;
  const claimedIds = new Set(
    edits
      .filter((e) => e.sourceBlockId != null)
      .map((e) => e.sourceBlockId)
  );
  if (claimedIds.size === 0) return candidates;
  return candidates.filter((block) => !claimedIds.has(block.id));
}
